/**
 * The folder page's Filter by pane: the record attributes and form questions a folder's records can be narrowed by, each
 * value with a count.
 *
 * Ed, 2026-11-03: "a UI where the user selects Folders or Filter By. If Folders, then the user sees the folder
 * structure, if Filter by, then the typical Filter by pane". So this draws with the record lists' own rows, ranges and
 * section rules (exported from ObjectFilterSidebar) rather than a look of its own.
 *
 * EVERY NUMBER IS THE SERVER'S. Search Folder counts each filter over what the OTHER filters leave, so a count says
 * what ticking it would show; this pane only draws them, and draws a ticked value even at zero so it can be unticked.
 *
 * FORM QUESTIONS WAIT FOR ONE TYPE, because a folder holds many types and each has its own forms; until one is
 * ticked the pane says so instead of offering nothing silently.
 */
import React from "react";
import {Box, Typography} from "@mui/material";

import {FacetLabel, FilterRow, RangeRow, SectionHeader} from "../Foundation/ObjectFilterSidebar";
import {classificationByCode} from "../../util/objectClassifications";
import {Blue3, Grey5} from "../../theme/styles";

type Row = Record<string, any>;
export type FolderFilters = {values: Record<string, string[]>; ranges: Record<string, {from: string; to: string}>};

export const NO_FILTERS: FolderFilters = {values: {}, ranges: {}};

/** Whether anything is being filtered. */
export function filtering(filters: FolderFilters) {
    return Object.values(filters.values).some(one => one.length > 0)
        || Object.values(filters.ranges).some(one => !!(one.from || one.to));
}

/** The filters as Search Folder reads them: only what is set, bounds under their server names. */
export function filtersPayload(filters: FolderFilters) {
    const Values = Object.fromEntries(Object.entries(filters.values).filter(([, chosen]) => chosen.length));
    const Ranges = Object.fromEntries(Object.entries(filters.ranges)
        .filter(([, span]) => span.from || span.to)
        .map(([key, span]) => [key, {From: span.from || null, To: span.to || null}]));
    return {Values, Ranges};
}

/** Tick or untick one value; choosing a different Type also drops the old type's form answers, which no longer apply. */
export function toggled(filters: FolderFilters, key: string, value: string): FolderFilters {
    const chosen = filters.values[key] ?? [];
    const next = chosen.includes(value) ? chosen.filter(one => one !== value) : [...chosen, value];
    const values = {...filters.values, [key]: next};
    if (key !== "Classification") return {...filters, values};
    const keep = (name: string) => !name.startsWith("Form:");
    return {values: Object.fromEntries(Object.entries(values).filter(([name]) => keep(name))),
            ranges: Object.fromEntries(Object.entries(filters.ranges).filter(([name]) => keep(name)))};
}

function label(filter: Row, value: Row) {
    if (filter.Key === "Classification") return classificationByCode(value.Value)?.singular ?? value.Label;
    return value.Label ?? value.Value;
}

function ValueSection({filter, filters, onToggle}: {filter: Row; filters: FolderFilters; onToggle: (key: string, value: string) => void}) {
    const chosen = filters.values[filter.Key] ?? [];
    if (!(filter.Values ?? []).length) return null;
    return <>
        <FacetLabel text={filter.Label}/>
        {(filter.Values ?? []).map((one: Row) => (
            <FilterRow key={one.Value} label={label(filter, one)} title={label(filter, one)} count={one.Count}
                       checked={chosen.includes(one.Value)} onToggle={() => onToggle(filter.Key, one.Value)}/>
        ))}
        {filter.Truncated ? <Typography sx={{fontSize: 10, color: Grey5, pl: "19px"}}>Showing {filter.Values.length} of {filter.DistinctValues}</Typography> : null}
    </>;
}

function RangeSection({filter, filters, onRange}: {
    filter: Row; filters: FolderFilters; onRange: (key: string, bound: "from" | "to", value: string) => void;
}) {
    const span = filters.ranges[filter.Key] ?? {from: "", to: ""};
    if (!filter.Answered && !span.from && !span.to) return null;
    return <>
        <FacetLabel text={filter.Label}/>
        <RangeRow facet={{...filter, Code: filter.Key, Values: []} as any} from={span.from} to={span.to}
                  onChange={(bound, value) => onRange(filter.Key, bound, value)}/>
    </>;
}

export default function FolderFilterPane({answer, filters, onToggle, onRange, onClear}: {
    answer: Row | null; filters: FolderFilters;
    onToggle: (key: string, value: string) => void;
    onRange: (key: string, bound: "from" | "to", value: string) => void;
    onClear: () => void;
}) {
    const all: Row[] = answer?.Filters ?? [];
    const values = all.filter(one => one.Kind === "values");
    const dates = all.filter(one => one.Kind === "range");
    const form: Row[] = answer?.FormFilters ?? [];
    const singular = answer?.FormFor ? (classificationByCode(answer.FormFor)?.singular ?? answer.FormFor) : null;
    return (
        <Box sx={{px: 0.5}}>
            <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600}}>Filter by</Typography>
                {filtering(filters) ? (
                    <Typography onClick={onClear} sx={{fontSize: 11, color: Blue3, cursor: "pointer"}}>Clear</Typography>
                ) : null}
            </Box>
            {!answer ? <Typography sx={{fontSize: 11, color: Grey5, mt: 1}}>Counting…</Typography> : <>
                <SectionHeader text="Record"/>
                {values.map(one => <ValueSection key={one.Key} filter={one} filters={filters} onToggle={onToggle}/>)}
                <SectionHeader text="Dates"/>
                {dates.map(one => <RangeSection key={one.Key} filter={one} filters={filters} onRange={onRange}/>)}
                <SectionHeader text={singular ? `Form: ${singular}` : "Form"}/>
                {!singular ? (
                    <Typography sx={{fontSize: 11, color: Grey5, lineHeight: 1.5}}>Tick one Type to filter by the questions on its form.</Typography>
                ) : !form.length ? (
                    <Typography sx={{fontSize: 11, color: Grey5, lineHeight: 1.5}}>{singular} forms here have no questions to filter by.</Typography>
                ) : form.map(one => one.Kind === "range"
                    ? <RangeSection key={one.Key} filter={one} filters={filters} onRange={onRange}/>
                    : <ValueSection key={one.Key} filter={one} filters={filters} onToggle={onToggle}/>)}
                {answer.FormFiltersTruncated ? (
                    <Typography sx={{fontSize: 10, color: Grey5, mt: 1}}>Showing {form.length} of {answer.FormFiltersAvailable} questions.</Typography>
                ) : null}
            </>}
        </Box>
    );
}
